import {activatePopup} from "@Reducers/pageSlice";
import useSingleTrackingEvent from "@Events/hooks/useSingleTrackingEvent";
import SubmitEmailModel from "@Events/models/SubmitEmailEventModel";
import validateEmail from "@User/functions/validateEmail";
import ajaxHandler from "@Ajax/v2/ajaxHandler";
import {useTranslation} from "@Translation/hooks/useTranslation";
import {useAppDispatch} from "@Hooks/dispatch/useAppDispatch";
import ParamsType from "@SearchResults/types/params/Params.type";
import SearchResultsUrlsType from "@SearchResults/types/urls/SearchResultsUrls.type";

interface UseCollectEmailForUpdatesAttributes {
    params: ParamsType,
    urls: SearchResultsUrlsType,
    email: {
        value: string,
        updateValue: (value: string) => void
    },
    terms: {
        value: boolean,
        set: (value: boolean) => void,
    }
}

export default function useCollectEmailForUpdates({params, urls, email, terms}: UseCollectEmailForUpdatesAttributes) {
    const {
        date,
        departureStation: {
            id: dp,
        },
        destinationStation: {
            id: ds,
        },
    } = params

    const {
        no_results_email: noResultsEMail,
    } = urls

    const {
        general_terms: generalTermsLabel,
        privacy_policy: privacyPolicyLabel,
    } = useTranslation("menu.footer")

    const {
        you_must_accept: youMustAcceptLabel,
        and: andLabel,
    } = useTranslation("sys")

    const {
        email_entered: emailEnteredLabel,
    } = useTranslation("no_res")

    const invalidEmailLabel = useTranslation("log_in.invalid_email")

    const dispatch = useAppDispatch()
    const dispatchSubmitEmailEvent = useSingleTrackingEvent(
        "search_trip_unavailability_email",
        SubmitEmailModel(email.value)
    )

    const activatePopupMessage = (message, title = 'Error') => {
        dispatch(activatePopup({
            message,
            title,
        }))
    }

    const submitEmail = async () => {
        const mustAcceptTerms = `${youMustAcceptLabel} ${generalTermsLabel} ${andLabel} ${privacyPolicyLabel}`
        if (!terms.value) {
            activatePopupMessage(mustAcceptTerms)
        } else if (!email.value) {
            activatePopupMessage(invalidEmailLabel)
        } else {
            const emailValid = validateEmail(email.value)
            const params = {
                dp,
                ds,
                date,
                mail: email.value,
            };

            if (emailValid) {
                dispatchSubmitEmailEvent()

                const response = await ajaxHandler({
                    url: noResultsEMail,
                    params,
                    type: "post",
                    stringify: true,
                })
                if (response?.data?.message) {
                    activatePopupMessage(emailEnteredLabel, 'Success')
                    email.updateValue('')
                    terms.set(false)
                }
            } else {
                activatePopupMessage(invalidEmailLabel)
            }
        }
    }

    return {
        submitEmail,
    }
}